import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { sourcesApi } from '@/lib/api';
import type { RssPollResult } from '@/types/api';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  sourceId: string;
  sourceUrl: string;
}

const STATUS_VARIANTS: Record<RssPollResult['status'], 'info' | 'success' | 'warning' | 'error'> = {
  running: 'info',
  ok: 'success',
  fetch_failed: 'warning',
  error: 'error',
};

export function RssPollHistoryDialog({ open, onOpenChange, sourceId, sourceUrl }: Props) {
  const { t, i18n } = useTranslation(['studio', 'common']);

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'sources', sourceId, 'poll-history'],
    queryFn: () => sourcesApi.pollHistory(sourceId, 50),
    enabled: open,
    // Polls in flight finish in seconds — refresh while drawer is open
    // so the "running" row flips to "ok" without a manual reload.
    refetchInterval: open ? 5000 : false,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('studio:sources.poll.historyTitle')}</DialogTitle>
        </DialogHeader>
        <p className="break-all font-mono text-small text-slate-500">{sourceUrl}</p>

        {isLoading && (
          <p className="py-4 text-small text-slate-500">{t('common:status.loading')}</p>
        )}

        {!isLoading && (data?.length ?? 0) === 0 && (
          <p className="py-6 text-center text-body text-slate-500">
            {t('studio:sources.poll.noHistory')}
          </p>
        )}

        {(data?.length ?? 0) > 0 && (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-small">
              <thead className="sticky top-0 bg-slate-50 text-h4 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">{t('studio:sources.poll.columns.startedAt')}</th>
                  <th className="px-3 py-2 text-left">{t('studio:sources.poll.columns.status')}</th>
                  <th className="px-3 py-2 text-right">{t('studio:sources.poll.columns.found')}</th>
                  <th className="px-3 py-2 text-right">{t('studio:sources.poll.columns.new')}</th>
                  <th className="px-3 py-2 text-right">{t('studio:sources.poll.columns.ingested')}</th>
                  <th className="px-3 py-2 text-left">{t('studio:sources.poll.columns.triggered')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data!.map((poll) => (
                  <tr key={poll.poll_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">
                      {new Date(poll.started_at).toLocaleString(i18n.language)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={STATUS_VARIANTS[poll.status]}>
                        {t(`studio:sources.poll.status.${poll.status}`)}
                      </Badge>
                      {poll.detail && (
                        <p
                          className="mt-1 max-w-md truncate text-xs text-slate-500"
                          title={poll.detail}
                        >
                          {poll.detail}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{poll.items_found}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{poll.items_new}</td>
                    <td className="px-3 py-2 text-right font-medium text-slate-900">
                      {poll.items_ingested}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {t(`studio:sources.poll.triggeredBy.${poll.triggered_by}`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
